<!-- Polaris (fase 5.3): the Details tab of the contact drawer (R2-5-PC-2 = B), the woot's contact page in the kit's
     PersonPanel order: who (avatar with upload, name, identifier, created · active, labels), what can be done with
     them (send a message, call, block: the old page header's actions, R2-5-PC-6 = A), the form, always editable
     with "Update contact" at the end (R2-5-PC-3 = A), the sections that fold (Attributes, Media, Merge; Q1,
     fase 5.4) and, for administrators, the danger zone with the delete dialog (D-G6). `isDirty` tells the drawer's discard guard that the form changed since it loaded
     (R2-5-PC-4 = B). -->
<script setup>
import { computed, ref, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { DataTable16, Image16, Merge16, SendAlt16 } from '@carbon/icons-vue';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import Policy from 'dashboard/components/policy.vue';
import { refreshContactInboxes } from 'dashboard/components-next/Contacts/ContactPanel/openContactRecord';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

// The sections that fold (Q1): Attributes open, Media and Merge closed (Media loads when it opens; Merge is rare
// and destructive, so it waits by the danger zone, R2-5-PC-2 = B).
const isAttributesOpen = ref(true);
const isMediaOpen = ref(false);
const isMergeOpen = ref(false);

// What the form held when it loaded (or last saved): the discard guard compares with it.
const savedForm = ref('');
const formSnapshot = () => JSON.stringify(contactsFormRef.value?.state ?? {});
const isDirty = computed(
  () => !!contactsFormRef.value && formSnapshot() !== savedForm.value
);

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(async () => {
  Object.assign(contactData.value, getInitialContactData());
  await nextTick();
  savedForm.value = formSnapshot();
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const isContactBlocked = computed(() => props.selectedContact?.blocked);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    // The contactable inboxes again, as a record only (contacts/fetchContactableInbox would add a contact that
    // is not on the list's page as an extra row).
    await refreshContactInboxes(store, props.selectedContact.id);
    savedForm.value = formSnapshot();
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

// The old page header's Block/Unblock, same messages.
const toggleContactBlock = async () => {
  const isBlocked = isContactBlocked.value;
  try {
    await store.dispatch('contacts/update', {
      ...props.selectedContact,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      isBlocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE')
    );
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};

defineExpose({ isDirty });
</script>

<template>
  <div class="grid grid-cols-1 gap-6 p-4" data-contact-details>
    <section
      class="grid grid-cols-1 gap-3"
      :aria-label="t('CONTACTS_POLARIS.DRAWER.IDENTITY')"
    >
      <div class="flex min-w-0 items-start gap-3">
        <Avatar
          :src="avatarSrc || ''"
          :name="selectedContact?.name || ''"
          :size="72"
          allow-upload
          class="shrink-0"
          @upload="handleAvatarUpload"
          @delete="handleAvatarDelete"
        />
        <div class="grid min-w-0 flex-1 gap-1 pt-1">
          <h2
            class="break-words type-polaris-card-title text-polaris-foreground"
          >
            {{ selectedContact?.name }}
          </h2>
          <p
            v-if="selectedContact?.identifier"
            class="flex min-w-0 items-start gap-1 break-all type-polaris-caption text-polaris-muted-foreground"
          >
            <span class="i-ph-user-gear mt-0.5 size-3.5 shrink-0" />
            {{ selectedContact.identifier }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            <span aria-hidden="true">{{
              t('CONTACTS_POLARIS.DRAWER.DOT')
            }}</span>
            {{
              t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </p>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
      <div class="flex flex-wrap items-center gap-2" data-contact-actions>
        <ComposeConversation :contact-id="String(selectedContact.id)">
          <template #trigger>
            <Button size="sm">
              <SendAlt16 aria-hidden="true" />
              {{ t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE') }}
            </Button>
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="selectedContact?.phoneNumber"
          :contact-id="String(selectedContact.id)"
          :label="t('CONTACT_PANEL.CALL')"
          variant="outline"
          color="slate"
          size="sm"
        />
        <Button
          variant="outline"
          size="sm"
          :disabled="isUpdating"
          @click="toggleContactBlock"
        >
          {{
            isContactBlocked
              ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
              : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
          }}
        </Button>
      </div>
    </section>

    <section class="grid grid-cols-1 gap-4">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />
      <div>
        <Button
          size="sm"
          :disabled="isUpdating || isFormInvalid"
          data-contact-update
          @click="updateContact"
        >
          {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON') }}
        </Button>
      </div>
    </section>

    <FoldingGroup
      v-model:open="isAttributesOpen"
      :title="t('CONTACTS_LAYOUT.SIDEBAR.TABS.ATTRIBUTES')"
      data-contact-section="attributes"
    >
      <template #icon><DataTable16 /></template>
      <ContactCustomAttributes :selected-contact="selectedContact" />
    </FoldingGroup>
    <FoldingGroup
      v-model:open="isMediaOpen"
      :title="t('CONTACTS_POLARIS.DRAWER.MEDIA')"
      data-contact-section="media"
    >
      <template #icon><Image16 /></template>
      <ContactMedia />
    </FoldingGroup>
    <FoldingGroup
      v-model:open="isMergeOpen"
      :title="t('CONTACTS_LAYOUT.SIDEBAR.MERGE.TITLE')"
      data-contact-section="merge"
    >
      <template #icon><Merge16 /></template>
      <ContactMerge
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
        @reset-tab="isMergeOpen = false"
      />
    </FoldingGroup>

    <Policy :permissions="['administrator']">
      <section
        class="grid grid-cols-1 gap-3 border-0 border-t border-solid border-polaris-border pt-5"
        data-contact-danger-zone
      >
        <div class="grid gap-1">
          <h3 class="type-polaris-label text-polaris-foreground">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h3>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </p>
        </div>
        <div>
          <Button
            variant="destructive"
            size="sm"
            @click="openConfirmDeleteContactDialog"
          >
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </Button>
        </div>
      </section>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
